/**
 * Challenge Profile — Zod schemas and inferred TypeScript types.
 * All external data validated here. .catch() fallbacks for resilience.
 */

import { z } from 'zod/v4'

// ── Challenge statuses & enums ───────────────────────────────

export const CHALLENGE_TYPES = ['private', 'public', 'competition', 'platform_free', 'platform_pro'] as const
export const CHALLENGE_STATUSES = ['draft', 'upcoming', 'active', 'completed', 'cancelled'] as const
export const TIME_MODELS = ['fixed', 'evergreen'] as const
export const JOIN_POLICIES = ['before_start', 'first_3_days', 'anytime'] as const
export const GENDER_FILTERS = ['all', 'female', 'male'] as const
export const MEMBER_STATUSES = ['active', 'completed', 'dropped', 'left'] as const
export const PRIZE_TYPES = ['money', 'premium', 'telegram_stars', 'physical'] as const

// ── Sub-schemas ──────────────────────────────────────────────

export const ChallengeProfileCreatorSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable().catch(null),
  badge_type: z.string().nullable().catch(null),
  username: z.string().nullable().catch(null),
  is_premium: z.boolean().catch(false),
})

export const MemberPreviewSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable().catch(null),
  badge_type: z.string().nullable().catch(null),
})

export const ChallengeHabitSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  icon: z.string().catch('📌'),
  category: z.string().nullable().catch(null),
  frequency: z.string().catch('daily'),
  weekly_days: z.array(z.number()).nullable().catch(null),
  custom_interval: z.number().nullable().catch(null),
  time_mode: z.string().nullable().catch(null),
  time_start: z.string().nullable().catch(null),
  time_duration: z.number().nullable().catch(null),
  value_required: z.boolean().catch(false),
  value_target: z.number().nullable().catch(null),
  value_unit: z.string().nullable().catch(null),
  proof_types: z.array(z.string()).nullable().catch(null),
  sort_order: z.number().catch(0),
})

export const ChallengeHabitProgressSchema = z.object({
  habit_id: z.string().uuid(),
  completed_count: z.number().catch(0),
  total_applicable: z.number().catch(1),
  completion_rate: z.number().catch(0),
})

export const ChallengeMembershipSchema = z.object({
  id: z.string().uuid(),
  status: z.string().catch('active'),
  attempt_number: z.number().catch(1),
  completion_rate: z.number().nullable().catch(0),
  joined_at: z.string(),
  personal_start_date: z.string().nullable().catch(null),
  personal_end_date: z.string().nullable().catch(null),
  completed_at: z.string().nullable().catch(null),
})

export const LeaderboardEntrySchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable().catch(null),
  badge_type: z.string().nullable().catch(null),
  completion_rate: z.number().catch(0),
  completed_count: z.number().catch(0),
  rank: z.number().catch(0),
})

export const ReviewsSummarySchema = z.object({
  avg_rating: z.number().nullable().catch(null),
  total_count: z.number().catch(0),
  distribution: z.array(z.number().catch(0)).catch([0, 0, 0, 0, 0]),
})

export const UserReviewSchema = z.object({
  id: z.string().uuid(),
  rating: z.number().min(1).max(5),
  text: z.string().nullable().catch(null),
  created_at: z.string(),
})

export const PrizeSchema = z.object({
  place_from: z.number(),
  place_to: z.number(),
  prize_type: z.string().catch('physical'),
  prize_value: z.string().catch(''),
  prize_description: z.string().nullable().catch(null),
})

export const SponsorSchema = z.object({
  id: z.string().uuid(),
  logo_url: z.string().nullable().catch(null),
  link_url: z.string().nullable().catch(null),
  sort_order: z.number().catch(0),
  is_active: z.boolean().catch(true),
})

export const JoinConditionsSchema = z.object({
  follow_creator: z.boolean().catch(false),
  like_challenge: z.boolean().catch(false),
  write_comment: z.boolean().catch(false),
  share_friends: z.boolean().catch(false),
  repost: z.boolean().catch(false),
}).catch({
  follow_creator: false,
  like_challenge: false,
  write_comment: false,
  share_friends: false,
  repost: false,
})

// ── Challenge detail schema (from RPC) ───────────────────────

export const ChallengeDetailSchema = z.object({
  id: z.string().uuid(),
  creator_id: z.string().uuid(),
  type: z.string().catch('private'),
  time_model: z.string().catch('fixed'),
  name: z.string(),
  description: z.string().nullable().catch(null),
  category_id: z.string().nullable().catch(null),
  cover_image_url: z.string().nullable().catch(null),
  cover_emoji: z.string().nullable().catch(null),
  cover_gradient: z.string().nullable().catch(null),
  link_title: z.string().nullable().catch(null),
  link_url: z.string().nullable().catch(null),
  tags: z.array(z.string()).catch([]),
  start_date: z.string().nullable().catch(null),
  end_date: z.string().nullable().catch(null),
  duration_days: z.number().catch(30),
  gender_filter: z.string().catch('all'),
  max_participants: z.number().nullable().catch(null),
  creator_participates: z.boolean().catch(true),
  join_policy: z.string().catch('before_start'),
  is_paid: z.boolean().catch(false),
  price: z.number().nullable().catch(null),
  commission_rate: z.number().catch(0.25),
  join_conditions: JoinConditionsSchema,
  is_hidden: z.boolean().catch(false),
  members_count: z.number().catch(0),
  avg_rating: z.number().nullable().catch(null),
  reviews_count: z.number().catch(0),
  status: z.string().catch('upcoming'),
  short_code: z.string().nullable().catch(null),
  created_at: z.string().catch(''),
  updated_at: z.string().nullable().catch(null),
})

// ── Full profile response ────────────────────────────────────

export const ChallengeProfileResponseSchema = z.object({
  challenge: ChallengeDetailSchema,
  creator: ChallengeProfileCreatorSchema,
  membership: ChallengeMembershipSchema.nullable().catch(null),
  members_preview: z.array(MemberPreviewSchema).catch([]),
  habits: z.array(ChallengeHabitSchema).catch([]),
  user_habit_progress: z.array(ChallengeHabitProgressSchema).catch([]),
  leaderboard_top3: z.array(LeaderboardEntrySchema).catch([]),
  reviews_summary: ReviewsSummarySchema,
  user_review: UserReviewSchema.nullable().catch(null),
  prizes: z.array(PrizeSchema).catch([]),
  sponsors: z.array(SponsorSchema).catch([]),
})

// ── Review list item ─────────────────────────────────────────

export const ReviewItemSchema = z.object({
  id: z.string().uuid(),
  rating: z.number().min(1).max(5),
  text: z.string().nullable().catch(null),
  created_at: z.string(),
  user_id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable().catch(null),
  badge_type: z.string().nullable().catch(null),
})

// ── Update params ────────────────────────────────────────────

export const CHALLENGE_UPDATE_FIELDS = [
  'name', 'description', 'cover_image_url', 'cover_emoji', 'cover_gradient',
  'link_title', 'link_url', 'tags', 'category_id',
  'start_date', 'end_date', 'duration_days', 'gender_filter',
  'max_participants', 'join_policy', 'is_paid', 'price',
  'join_conditions', 'is_hidden', 'creator_participates',
] as const

// ── Inferred types ───────────────────────────────────────────

export type ChallengeProfileCreator = z.infer<typeof ChallengeProfileCreatorSchema>
export type MemberPreview = z.infer<typeof MemberPreviewSchema>
export type ChallengeHabit = z.infer<typeof ChallengeHabitSchema>
export type ChallengeHabitProgress = z.infer<typeof ChallengeHabitProgressSchema>
export type LeaderboardEntry = z.infer<typeof LeaderboardEntrySchema>
export type ReviewsSummary = z.infer<typeof ReviewsSummarySchema>
export type UserReview = z.infer<typeof UserReviewSchema>
export type Prize = z.infer<typeof PrizeSchema>
export type Sponsor = z.infer<typeof SponsorSchema>
export type JoinConditions = z.infer<typeof JoinConditionsSchema>
export type ChallengeDetail = z.infer<typeof ChallengeDetailSchema>
export type ChallengeMembership = z.infer<typeof ChallengeMembershipSchema>
export type ChallengeProfileResponse = z.infer<typeof ChallengeProfileResponseSchema>
export type ReviewItem = z.infer<typeof ReviewItemSchema>
